/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@/shared/ui/radix-icons';
import { UnstyledButton } from '@/shared/ui';

interface AgendaAddFabProps {
  onClick: () => void;
}

/**
 * Floating add-appointment button for the mobile agenda, pinned just above the
 * bottom navigation bar (offset by `--bottom-nav-height` + the iOS safe area).
 * Brand-accent circular FAB, token-driven to match the platform styling.
 *
 * @param props Click handler that opens the create-event flow.
 * @returns Rendered floating action button.
 */
export const AgendaAddFab: React.FC<AgendaAddFabProps> = ({ onClick }) => {
  const { t } = useTranslation('features/agenda');
  return (
    <UnstyledButton
      type="button"
      data-testid="agenda-add-fab"
      aria-label={t('mobile.add')}
      onClick={onClick}
      className="fixed right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full text-[var(--button-primary-text)] transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)] focus-visible:ring-offset-2"
      style={{
        bottom: 'calc(var(--bottom-nav-height, 52px) + env(safe-area-inset-bottom, 0px) + var(--space-4))',
        background: 'var(--seed-base)',
        boxShadow: 'var(--shadow-md, var(--shadow-overflow-sheet))',
        color: 'var(--button-primary-text, #fff)',
      }}
    >
      <PlusIcon width={24} height={24} />
    </UnstyledButton>
  );
};
